import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { cachedResponse, useRefreshVersion } from './store';
import {
  Avatar, Badge, Drawer, Empty, ErrorState, Field, Loading, OwnerSelect, Select, date, dateInputToIso, dayOnly, isoToDateInput,
  priorityTone, severityTone, titleCase, useAssignees, useCan, useMe, useToast,
} from './ui';

const COLUMNS = [
  ['todo', 'To do', '○'], ['in_progress', 'In progress', '◐'], ['blocked', 'Blocked', '◈'], ['done', 'Done', '●'],
];
const PRIORITIES = ['urgent', 'high', 'medium', 'low'];
const PAGE_SIZE = 100;

const isOverdue = (task) => task.due_at && !['done', 'cancelled'].includes(task.status) && new Date(task.due_at) < new Date();

export function Tasks() {
  const can = useCan();
  const me = useMe();
  const assignees = useAssignees();
  const defaultTaskKey = `/tasks?limit=${PAGE_SIZE}&offset=0`;
  const cachedTasks = cachedResponse(defaultTaskKey);
  const [items, setItems] = useState(cachedTasks?.items ?? null);
  const [page, setPage] = useState(0);
  const [pageInfo, setPageInfo] = useState(cachedTasks?.page ?? { total: 0 });
  const [error, setError] = useState('');
  const [view, setView] = useState('board');
  const [filters, setFilters] = useState({ search: '', owner: '', priority: '', mine: false, showCancelled: false });
  const [openId, setOpenId] = useState(null);
  const [quickAdd, setQuickAdd] = useState(null); // column id being added to
  const [quickTitle, setQuickTitle] = useState('');
  const [dragging, setDragging] = useState(null);
  const [overColumn, setOverColumn] = useState(null);
  // The card the pointer is over while dragging, so a drop lands *before* it.
  const [overCard, setOverCard] = useState(null);
  const [toast, toastNode] = useToast();
  const refreshVersion = useRefreshVersion();

  const load = useCallback(async () => {
    setError('');
    try {
      const result = await api(`/tasks?limit=${PAGE_SIZE}&offset=${page * PAGE_SIZE}`);
      setItems(result.items ?? []); setPageInfo(result.page ?? { total: 0 });
    } catch (caught) { setError(caught.message); }
  }, [page, refreshVersion]);
  useEffect(() => { void load(); }, [load]);

  const visible = useMemo(() => (items ?? []).filter((t) => {
    if (!filters.showCancelled && t.status === 'cancelled') return false;
    if (filters.mine && t.owner_user_id !== me?.user?.id) return false;
    if (filters.owner && t.owner_user_id !== filters.owner) return false;
    if (filters.priority && t.priority !== filters.priority) return false;
    const q = filters.search.trim().toLowerCase();
    if (q && !`${t.title} ${t.description ?? ''} ${t.incident_key ?? ''} ${t.project_title ?? ''}`.toLowerCase().includes(q)) return false;
    return true;
  }), [items, filters, me]);

  const update = async (task, body, message) => {
    // Optimistic: move the card now, roll back if the server disagrees.
    const previous = items;
    setItems((list) => list.map((t) => (t.id === task.id ? { ...t, ...body } : t)));
    try { await api(`/tasks/${task.id}`, { method: 'PATCH', body: JSON.stringify(body) }); if (message) toast(message); await load(); }
    catch (caught) { setItems(previous); toast(caught.message, 'danger'); }
  };
  const create = async (body) => {
    try { const created = await api('/tasks', { method: 'POST', body: JSON.stringify(body) }); await load(); toast('Task added'); return created.id; }
    catch (caught) { toast(caught.message, 'danger'); return null; }
  };
  const remove = async (task) => {
    if (!window.confirm(`Delete "${task.title}"? This cannot be undone.`)) return false;
    try { await api(`/tasks/${task.id}`, { method: 'DELETE' }); await load(); toast('Task deleted'); return true; }
    catch (caught) { toast(caught.message, 'danger'); return false; }
  };
  // Stays open after a successful add: a to-do list is written several
  // items at a time, and re-opening the form for each one was the complaint.
  // Escape or Cancel closes it.
  const submitQuick = async (event) => {
    event.preventDefault();
    if (quickTitle.trim().length < 3) return;
    if (await create({ title: quickTitle.trim(), status: quickAdd })) setQuickTitle('');
  };
  /**
   * Where a card lands. `before` is the card it was dropped onto (it goes
   * above it) or null for the end of the column. The new position is the
   * midpoint of its neighbours' sort_order, so nothing else is renumbered;
   * the numbers only have to be distinct and ordered, not tidy.
   */
  const onDrop = (status, before = null) => (event) => {
    event.preventDefault(); event.stopPropagation();
    setOverColumn(null); setOverCard(null);
    const task = items?.find((t) => t.id === (dragging ?? event.dataTransfer.getData('text/plain')));
    setDragging(null);
    if (!task || (before && before.id === task.id)) return;
    const column = (items ?? []).filter((t) => t.status === status && t.id !== task.id).sort((a, b) => a.sort_order - b.sort_order);
    const index = before ? column.findIndex((t) => t.id === before.id) : column.length;
    const prev = column[index - 1]?.sort_order;
    const next = column[index]?.sort_order;
    const sortOrder = prev != null && next != null ? (prev + next) / 2 : prev != null ? prev + 1 : next != null ? next - 1 : 0;
    const body = { sortOrder, ...(task.status !== status ? { status } : null) };
    void update(task, body, task.status !== status ? `→ ${titleCase(status)}` : null);
  };

  const set = (key) => (event) => setFilters((f) => ({ ...f, [key]: event.target.type === 'checkbox' ? event.target.checked : event.target.value }));
  const openTask = items?.find((t) => t.id === openId);
  const overdue = visible.filter(isOverdue).length;

  const card = (t) => <article key={t.id} className={`task-card ${dragging === t.id ? 'is-dragging' : ''} ${overCard === t.id && dragging !== t.id ? 'is-target' : ''} ${isOverdue(t) ? 'is-overdue' : ''}`}
    draggable={can('operate_tasks')} onDragStart={(e) => { setDragging(t.id); e.dataTransfer.setData('text/plain', t.id); e.dataTransfer.effectAllowed = 'move'; }} onDragEnd={() => { setDragging(null); setOverCard(null); }}
    onDragOver={(e) => { if (can('operate_tasks') && dragging && dragging !== t.id) { e.preventDefault(); e.stopPropagation(); e.dataTransfer.dropEffect = 'move'; setOverCard(t.id); setOverColumn(t.status); } }}
    onDragLeave={() => setOverCard((c) => (c === t.id ? null : c))} onDrop={onDrop(t.status, t)}
    onClick={() => setOpenId(t.id)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpenId(t.id)}>
    <div className="task-card-top"><Badge tone={priorityTone(t.priority)}>{t.priority}</Badge>{t.incident_key && <span className={`chip ${severityTone(t.incident_severity)}`} title={t.incident_title}>{t.incident_key}</span>}</div>
    <b>{t.title}</b>
    {t.description && <p>{t.description}</p>}
    <div className="task-card-foot">
      {t.owner_user_id ? <span className="owner"><Avatar name={t.owner_name} email={t.owner_email} size={22} />{t.owner_name || t.owner_email}</span> : <span className="owner unassigned">Unassigned</span>}
      {t.due_at && <span className={`due ${isOverdue(t) ? 'late' : ''}`}>{isOverdue(t) ? '⚠ ' : '◷ '}{dayOnly(t.due_at)}</span>}
    </div>
  </article>;

  return <>
    <section className="page-title"><div><div className="eyebrow">Tasks</div><h1>Tasks</h1><p>Owners, due dates and follow-up work, kept next to the issue they came from.</p></div>
      <div className="btn-row"><div className="segmented"><button className={view === 'board' ? 'on' : ''} onClick={() => setView('board')}>Board</button><button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}>List</button></div>{can('operate_tasks') && <button className="primary" onClick={() => { setQuickAdd('todo'); setOpenId('new'); }}>+ New task</button>}</div></section>

    <section className="toolbar">
      <input value={filters.search} onChange={set('search')} placeholder="Search tasks, incidents, projects" />
      <Select value={filters.owner} onChange={set('owner')} label="Owner" options={[['', 'Any owner'], ...assignees.map((a) => [a.user_id, a.full_name || a.email])]} />
      <Select value={filters.priority} onChange={set('priority')} label="Priority" options={[['', 'Any priority'], ...PRIORITIES.map((p) => [p, titleCase(p)])]} />
      <label className="check-inline"><input type="checkbox" checked={filters.mine} onChange={set('mine')} /> Mine</label>
      <label className="check-inline"><input type="checkbox" checked={filters.showCancelled} onChange={set('showCancelled')} /> Cancelled</label>
      {overdue > 0 && <span className="sev danger"><b>{overdue}</b> overdue</span>}
    </section>

    {pageInfo.total > PAGE_SIZE && <div className="pager admin-list-pager"><button className="subtle" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>← Prev</button><span>Page {page + 1} of {Math.ceil(pageInfo.total / PAGE_SIZE)}</span><button className="subtle" disabled={(page + 1) * PAGE_SIZE >= pageInfo.total} onClick={() => setPage((p) => p + 1)}>Next →</button></div>}

    {error ? <ErrorState message={error} retry={load} /> : !items ? <Loading /> : view === 'board' ? (
      <div className="board">
        {COLUMNS.map(([status, label, icon]) => {
          const column = visible.filter((t) => t.status === status).sort((a, b) => a.sort_order - b.sort_order);
          return <section key={status} className={`board-col ${overColumn === status ? 'is-over' : ''}`}
            onDragOver={(e) => { if (can('operate_tasks')) { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setOverColumn(status); } }}
            onDragLeave={() => setOverColumn((c) => (c === status ? null : c))} onDrop={onDrop(status)}>
            <header><span className={`col-icon ${status}`}>{icon}</span><b>{label}</b><small>{column.length}</small>{can('operate_tasks') && <button className="icon-button" title="Add task" onClick={() => { setQuickAdd(status); setQuickTitle(''); }}>＋</button>}</header>
            <div className="board-cards">
              {quickAdd === status && <form className="quick-card" onSubmit={submitQuick}><input autoFocus value={quickTitle} onChange={(e) => setQuickTitle(e.target.value)} onKeyDown={(e) => e.key === 'Escape' && setQuickAdd(null)} placeholder="Task title, then Enter" /><div className="btn-row"><button type="button" className="text-button" onClick={() => setQuickAdd(null)}>Done</button><button className="primary" disabled={quickTitle.trim().length < 3}>Add another</button></div></form>}
              {column.map(card)}
              {!column.length && quickAdd !== status && <div className="board-empty">{status === 'done' ? 'Nothing finished yet' : 'Drop tasks here'}</div>}
              {/* A landing strip below the last card, so "to the bottom" is a
                  drop target of its own rather than a fight with the last card. */}
              {dragging && column.length > 0 && <div className="board-tail" onDragOver={(e) => { e.preventDefault(); setOverCard(null); }} onDrop={onDrop(status)}>Move to end</div>}
            </div>
          </section>;
        })}
      </div>
    ) : (
      <article className="panel table-panel"><div className="table-wrap"><table><thead><tr><th>Task</th><th>Status</th><th>Priority</th><th>Owner</th><th>Due</th><th>Incident</th><th>Created</th></tr></thead><tbody>
        {visible.length ? visible.map((t) => <tr key={t.id} className="clickable" onClick={() => setOpenId(t.id)}>
          <td><b>{t.title}</b>{t.description && <small className="plain">{t.description}</small>}</td>
          <td>{can('operate_tasks') ? <Select size="sm" label="Status" value={t.status} onChange={(e) => update(t, { status: e.target.value })} options={[...COLUMNS.map(([s]) => s), 'cancelled'].map((s) => [s, titleCase(s)])} /> : titleCase(t.status)}</td>
          <td><Badge tone={priorityTone(t.priority)}>{t.priority}</Badge></td>
          <td>{t.owner_name || t.owner_email || <span className="unassigned">Unassigned</span>}</td>
          <td className={isOverdue(t) ? 'late' : ''}>{t.due_at ? dayOnly(t.due_at) : '—'}</td>
          <td>{t.incident_key ? <span className={`chip ${severityTone(t.incident_severity)}`}>{t.incident_key}</span> : '—'}</td>
          <td>{date(t.created_at)}</td>
        </tr>) : <tr><td colSpan="7"><Empty title="No tasks match" body="Clear a filter or add a task from the board." /></td></tr>}
      </tbody></table></div></article>
    )}

    {openId === 'new' && <TaskDrawer assignees={assignees} onClose={() => setOpenId(null)} onCreate={async (body) => { const id = await create(body); if (id) setOpenId(null); }} />}
    {openTask && <TaskDrawer task={openTask} assignees={assignees} onClose={() => setOpenId(null)} onSave={(body) => update(openTask, body, 'Task updated')} onDelete={async () => { if (await remove(openTask)) setOpenId(null); }} />}
    {toastNode}
  </>;
}

/** Full editor for one task; also the "new task" form when no task is given. */
function TaskDrawer({ task, assignees, onClose, onSave, onCreate, onDelete }) {
  const can = useCan();
  const [incidents, setIncidents] = useState([]);
  const [form, setForm] = useState({
    title: task?.title ?? '', description: task?.description ?? '', status: task?.status ?? 'todo', priority: task?.priority ?? 'medium',
    ownerUserId: task?.owner_user_id ?? null, dueAt: isoToDateInput(task?.due_at), incidentId: task?.incident_id ?? '',
  });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api('/incidents?limit=100').then((r) => setIncidents(r.items ?? [])).catch(() => setIncidents([])); }, []);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const editable = can('operate_tasks');
  const dirty = !task || form.title !== task.title || form.description !== (task.description ?? '') || form.status !== task.status || form.priority !== task.priority
    || form.ownerUserId !== (task.owner_user_id ?? null) || form.dueAt !== isoToDateInput(task.due_at) || form.incidentId !== (task.incident_id ?? '');

  const submit = async (event) => {
    event.preventDefault(); setBusy(true);
    const body = {
      title: form.title.trim(), description: form.description.trim() || null, status: form.status, priority: form.priority,
      ownerUserId: form.ownerUserId, dueAt: dateInputToIso(form.dueAt), incidentId: form.incidentId || null,
    };
    try {
      if (task) await onSave(body);
      else await onCreate({ ...body, description: body.description ?? undefined, ownerUserId: body.ownerUserId ?? undefined, dueAt: body.dueAt ?? undefined, incidentId: body.incidentId ?? undefined });
    } finally { setBusy(false); }
  };

  const linkedIncident = task?.incident_key ? incidents.find((i) => i.id === task.incident_id) : null;
  const footer = editable ? <>
    {task && <button className="text-button danger" type="button" onClick={onDelete}>Delete</button>}
    <span className="grow" />
    <button className="subtle" type="button" onClick={onClose}>{dirty ? 'Discard' : 'Close'}</button>
    <button className="primary" form="task-form" disabled={busy || !dirty || form.title.trim().length < 3}>{busy ? 'Saving…' : task ? 'Save changes' : 'Create task'}</button>
  </> : null;

  return <Drawer title={task ? 'Task' : 'New task'} subtitle={task ? `Created by ${task.creator_name || 'unknown'} · ${date(task.created_at)}` : 'OPERATIONAL TASK'} onClose={onClose} footer={footer}>
    <form id="task-form" className="form-stack" onSubmit={submit}>
      <input className="title-input" autoFocus={!task} value={form.title} onChange={set('title')} placeholder="Task title" disabled={!editable} />
      <div className="props">
        <div className="prop"><span>Status</span><Select value={form.status} onChange={set('status')} disabled={!editable} options={[...COLUMNS.map(([s, l]) => [s, l]), ['cancelled', 'Cancelled']]} /></div>
        <div className="prop"><span>Priority</span><Select value={form.priority} onChange={set('priority')} disabled={!editable} options={PRIORITIES.map((p) => [p, titleCase(p)])} /></div>
        <div className="prop"><span>Owner</span><OwnerSelect value={form.ownerUserId} onChange={(v) => setForm({ ...form, ownerUserId: v })} assignees={assignees} /></div>
        <div className="prop"><span>Due date</span><input type="date" value={form.dueAt} onChange={set('dueAt')} disabled={!editable} /></div>
        <div className="prop"><span>Incident</span><Select value={form.incidentId} onChange={set('incidentId')} disabled={!editable} options={[['', 'None'], ...(linkedIncident == null && task?.incident_key ? [[task.incident_id, `${task.incident_key} · ${task.incident_title}`]] : []), ...incidents.map((i) => [i.id, `${i.key} · ${i.title}`])]} /></div>
        {task?.project_title && <div className="prop"><span>Project</span><em>{task.project_title}</em></div>}
        {task?.completed_at && <div className="prop"><span>Completed</span><em>{date(task.completed_at)}</em></div>}
      </div>
      <Field label="Description"><textarea rows={8} value={form.description} onChange={set('description')} placeholder="Steps, links, context — anything the owner needs." disabled={!editable} /></Field>
    </form>
  </Drawer>;
}
